/**
 * usePrinterDiscovery
 *
 * Composable that integrates with GET {PRINT_AGENT}/devices?deviceType=printer
 * to fetch available printers from the device registry.
 *
 * Returns reactive state (printers, loading, error) and a `discover()`
 * function.  Results are cached per hospital so repeated calls within
 * the same session avoid redundant network trips.
 *
 * Usage:
 *   const { printers, printerOptions, isDiscovering, discoverError, discover } =
 *     usePrinterDiscovery();
 */
import { ref, computed } from 'vue';
import { getApiClient } from '@/core/services/api.service';
import { isAxiosError } from 'axios';
import { sanitizeMessage } from '@/core/utils/api-error.util';
import { config, API_ROUTES } from '@/core/config';
import { useLocale } from '@/shared/composables/useLocale';

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * Raw device shape returned by GET {PRINT_AGENT}/devices.
 *
 * Backend may return snake_case and/or camelCase fields.
 * - Backend-persisted fields (snake_case) can be null (mac_address, serial_number, etc.)
 * - Optional fields may be missing entirely
 * - `deviceId` is only present if the backend has already persisted a UUID; synthetic IDs
 *   created by getDeviceId() should NEVER be used in API calls (PATCH/DELETE).
 */
export interface DeviceApiRecord {
  // Snake_case shape (from backend persistence)
  device_id?: string;
  device_name?: string;
  device_type?: string;
  hospital_id?: string;
  agent_id?: string;
  mac_address?: string | null;
  serial_number?: string | null;
  location_description?: string | null;
  care_unit_id?: string | null;
  discovered_at?: string | null;
  printer_port?: number | null;
  dpi_source?: string | null;

  // CamelCase shape (for compatibility)
  deviceId?: string;
  agentId: string;
  hospitalId: string;
  deviceType: string;
  deviceName: string;
  macAddress: string;
  model: string | null;
  serialNumber: string | null;
  locationDescription: string | null;
  careUnitId: string | null;
  status: string;
  discoveredAt: string | null;
  printerPort: number | null;
  dpi: number | null;
  dpiSource: string | null;
}

function getValue(record: DeviceApiRecord, camel: keyof DeviceApiRecord, snake: keyof DeviceApiRecord): string {
  const camelValue = record[camel];
  if (typeof camelValue === 'string' && camelValue.trim().length > 0) return camelValue;
  const snakeValue = record[snake];
  if (typeof snakeValue === 'string' && snakeValue.trim().length > 0) return snakeValue;
  return '';
}

function getNullableValue(record: DeviceApiRecord, camel: keyof DeviceApiRecord, snake: keyof DeviceApiRecord): string | null {
  const camelValue = record[camel];
  if (typeof camelValue === 'string') return camelValue;
  const snakeValue = record[snake];
  if (typeof snakeValue === 'string') return snakeValue;
  return null;
}

/** API response envelope for GET {PRINT_AGENT}/devices. */
export interface DeviceListResponse {
  success: boolean;
  data: DeviceApiRecord[];
  count: number;
  limit: number;
  offset: number;
  timestamp: string;
}

/** Normalized printer record used throughout the UI. */
export interface DiscoveredPrinter {
  /** Unique device identifier (derived from agentId + deviceName or macAddress) */
  deviceId: string;
  /** Print-agent that reported this device */
  agentId: string;
  /** Hospital this device belongs to */
  hospitalId: string;
  /** Device category (e.g. "printer") */
  deviceType: string;
  /** Printer display name */
  name: string;
  /** Printer model */
  model: string | null;
  /** Printer serial number */
  serialNumber: string | null;
  /** MAC address of the device */
  macAddress: string;
  /** Human-readable location description */
  locationDescription: string | null;
  /** Care-unit this printer is assigned to (null for unassigned) */
  careUnitId: string | null;
  /** Printer status: "Online", "Offline", etc. */
  status: string;
  /** When the device was discovered */
  discoveredAt: string | null;
}

/** Convert a camelCase API record to a DiscoveredPrinter for UI consumption. */
function toDiscoveredPrinter(d: DeviceApiRecord): DiscoveredPrinter {
  const agentId = getValue(d, 'agentId', 'agent_id');
  const deviceName = getValue(d, 'deviceName', 'device_name');
  const macAddress = getValue(d, 'macAddress', 'mac_address');
  const backendDeviceId = getValue(d, 'device_id', 'device_id');

  return {
    // Prefer persisted backend device_id so print-jobs can target the correct device.
    deviceId: backendDeviceId || `${agentId}-${macAddress || deviceName}`,
    agentId,
    hospitalId: getValue(d, 'hospitalId', 'hospital_id'),
    deviceType: getValue(d, 'deviceType', 'device_type'),
    name: deviceName,
    model: getNullableValue(d, 'model', 'model'),
    serialNumber: getNullableValue(d, 'serialNumber', 'serial_number'),
    macAddress,
    locationDescription: getNullableValue(d, 'locationDescription', 'location_description'),
    careUnitId: getNullableValue(d, 'careUnitId', 'care_unit_id'),
    status: getValue(d, 'status', 'status'),
    discoveredAt: getNullableValue(d, 'discoveredAt', 'discovered_at'),
  };
}

// ── Module-level cache ─────────────────────────────────────────────────────

/** Cached results keyed by hospitalId so each hospital only discovers once. */
const cache = new Map<string, DiscoveredPrinter[]>();

/**
 * In-flight promise keyed by hospitalId.
 * Prevents redundant network requests when multiple consumers call discover()
 * concurrently — the second caller piggy-backs on the first request.
 */
const inflight = new Map<string, Promise<DiscoveredPrinter[]>>();

/** Shape returned by the printerOptions computed for use in PrimeVue dropdowns. */
export interface PrinterOption {
  /** Human-readable display string, e.g. "Zebra ZD421" */
  label: string;
  /** Underlying device ID (UUID) used when submitting a print job */
  value: string;
}

// ── Composable ─────────────────────────────────────────────────────────────

export function usePrinterDiscovery() {
  // Per-instance reactive state — each consumer gets its own refs so
  // concurrent dialogs never bleed loading/error state into each other.
  const printers = ref<DiscoveredPrinter[]>([]);
  const isDiscovering = ref(false);
  const discoverError = ref<string | null>(null);
  const { t } = useLocale();

  /** Dropdown-friendly options derived from this instance's printers list. */
  const printerOptions = computed<PrinterOption[]>(() =>
    printers.value.map((p) => {
      const suffix = p.locationDescription ? ` (${p.locationDescription})` : '';
      return { label: `${p.name}${suffix}`, value: p.deviceId };
    }),
  );

  /**
   * Fetch available printers from the device registry.
   *
   * @param force  When true, bypasses cache and re-fetches.
   */
  async function discover(force = false): Promise<void> {
    const hospitalId = config.hospitalId?.trim() || '';

    // Return cached results unless forced
    if (!force && cache.has(hospitalId)) {
      printers.value = cache.get(hospitalId) ?? [];
      return;
    }

    isDiscovering.value = true;
    discoverError.value = null;

    try {
      // De-duplicate concurrent network calls for the same hospital.
      // When force=true we always create a fresh promise, but we keep a
      // reference so the finally-block only removes its *own* entry —
      // preventing a forced call from accidentally evicting a later
      // caller's inflight promise.
      let pending = inflight.get(hospitalId);
      if (!pending || force) {
        pending = (async () => {
          const api = getApiClient();
          const resp = await api.get<DeviceApiRecord[]>(`${API_ROUTES.PRINT_AGENT}/devices`, {
            params: {
              deviceType: 'printer',
            },
          });

          // createApiClient's response interceptor unwraps the ApiResponse envelope,
          // so resp.data is normally already the DeviceApiRecord[] array.  If the
          // print-agent (or a proxy) returns the full envelope without a nested `data`
          // key, or returns a non-standard shape, fall back to resp.data.data so we
          // never silently lose devices when resp.data is an object with a data array.
          const rawDevices: DeviceApiRecord[] = Array.isArray(resp.data)
            ? resp.data
            : Array.isArray((resp.data as unknown as { data?: unknown })?.data)
              ? (resp.data as unknown as { data: DeviceApiRecord[] }).data
              : [];

          if (rawDevices.length > 0) {
            const devices = rawDevices.map(toDiscoveredPrinter);
            cache.set(hospitalId, devices);
            return devices;
          } else {
            // Success but no devices found
            return [] as DiscoveredPrinter[];
          }
        })();
        inflight.set(hospitalId, pending);
      }

      // Capture the exact promise we are awaiting so the finally-block
      // can check ownership before deleting.
      const myPending = pending;
      try {
        const devices = await myPending;
        printers.value = devices;
      } finally {
        // Only remove the inflight entry if it still points to the
        // promise *this* caller created/awaited.  A concurrent forced
        // caller may have already replaced it with a newer promise.
        if (inflight.get(hospitalId) === myPending) {
          inflight.delete(hospitalId);
        }
      }
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && '__discoveryFail' in err) {
        discoverError.value = (err as unknown as Error).message;
      } else if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 404) {
          discoverError.value = t('printer.notAvailable', 'Printers are not available');
        } else if (status === 503) {
          discoverError.value = t('apiErrors.serviceUnavailable', 'Service unavailable. Please try again later.');
        } else if (status === 400) {
          discoverError.value = t('printer.invalidRequest', 'Invalid request. Please contact support.');
        } else {
          const rawMsg = err.response?.data?.message ?? err.message;
          discoverError.value = rawMsg ? sanitizeMessage(rawMsg) : t('printer.notAvailable', 'Printers are not available');
        }
      } else {
        // Preserve the message from plain Errors (e.g. the interceptor throws
        // `new Error(backendMessage)` for success=false responses).
        // Route-not-found errors (e.g. "Route GET:/printAgent/... not found") are
        // shown as a friendly message instead of the raw Fastify route error.
        const msg = err instanceof Error ? err.message : '';
        const isRouteNotFound = /route .* not found/i.test(msg);
        discoverError.value = isRouteNotFound
          ? t('printer.notAvailable', 'Printers are not available')
          : (sanitizeMessage(msg) || t('printer.notAvailable', 'Printers are not available'));
      }
      printers.value = [];
    } finally {
      isDiscovering.value = false;
    }
  }

  return {
    /** Reactive list of discovered printers. */
    printers,
    /** Dropdown-friendly options (printer display names with location suffix). */
    printerOptions,
    /** Whether a fetch request is in-flight. */
    isDiscovering,
    /** Last error message, if any. */
    discoverError,
    /** Fetch available printers. Pass `true` to bypass cache. */
    discover,
  };
}
